import { Link, usePage } from '@inertiajs/react';
import {
    KeyRound,
    LayoutGrid,
    Library,
    Users,
} from 'lucide-react';
import AppLogo from '@/components/app-logo';
import { NavFooter } from '@/components/nav-footer';
import { NavMain } from '@/components/nav-main';
import { NavUser } from '@/components/nav-user';
import { TeamSwitcher } from '@/components/team-switcher';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useCurrentUrl } from '@/hooks/use-current-url';
import { dashboard } from '@/routes';
import { index as scenariosIndex } from '@/routes/studio/scenarios';
import { edit as teamEdit } from '@/routes/studio/team';
import { index as tokensIndex } from '@/routes/studio/tokens';
import type { NavItem, TeamSummary } from '@/types';

const footerNavItems: NavItem[] = [];

function resolveCurrentTeam(
    teams: TeamSummary[],
    pageUrl: string,
): TeamSummary | null {
    const url = new URL(
        pageUrl,
        typeof window !== 'undefined'
            ? window.location.origin
            : 'http://localhost',
    );

    const studioMatch = url.pathname.match(/^\/studio\/([^/]+)/);
    if (studioMatch && studioMatch[1] !== 'teams') {
        return (
            teams.find((team) => team.slug === studioMatch[1]) ??
            teams[0] ??
            null
        );
    }

    const teamSlug = url.searchParams.get('team');
    if (teamSlug) {
        return (
            teams.find((team) => team.slug === teamSlug) ?? teams[0] ?? null
        );
    }

    return teams[0] ?? null;
}

export function AppSidebar() {
    const page = usePage();
    const { auth } = page.props;
    const { isCurrentUrl } = useCurrentUrl();
    const teams = auth.user?.teams ?? [];
    const isStudioUser = Boolean(auth.user?.is_author || auth.user?.is_admin);
    const currentTeam = resolveCurrentTeam(teams, page.url);

    const mainNavItems: NavItem[] = [
        {
            title: 'Dashboard',
            href: dashboard(),
            icon: LayoutGrid,
        },
    ];

    const studioNavItems: NavItem[] = currentTeam
        ? [
              {
                  title: 'Scenarios',
                  href: scenariosIndex(currentTeam.slug),
                  icon: Library,
              },
              {
                  title: 'Team',
                  href: teamEdit(currentTeam.slug),
                  icon: Users,
              },
              {
                  title: 'API Tokens',
                  href: tokensIndex(currentTeam.slug),
                  icon: KeyRound,
              },
          ]
        : [];

    return (
        <Sidebar collapsible="icon" variant="inset">
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton size="lg" asChild>
                            <Link href={dashboard()} prefetch>
                                <AppLogo />
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
                {isStudioUser && <TeamSwitcher currentTeam={currentTeam} />}
            </SidebarHeader>

            <SidebarContent>
                <NavMain items={mainNavItems} />
                {isStudioUser && studioNavItems.length > 0 && (
                    <SidebarGroup className="px-2 py-0">
                        <SidebarGroupLabel>Studio</SidebarGroupLabel>
                        <SidebarMenu>
                            {studioNavItems.map((item) => (
                                <SidebarMenuItem key={item.title}>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={isCurrentUrl(
                                            item.href,
                                            undefined,
                                            true,
                                        )}
                                        tooltip={{ children: item.title }}
                                    >
                                        <Link href={item.href} prefetch>
                                            {item.icon && <item.icon />}
                                            <span>{item.title}</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    </SidebarGroup>
                )}
            </SidebarContent>

            <SidebarFooter>
                <NavFooter items={footerNavItems} className="mt-auto" />
                <NavUser />
            </SidebarFooter>
        </Sidebar>
    );
}
